import {
  ELEMENT_NAMES,
  ItemKind,
  ItemRarity,
  RARITY_COLORS,
  SKILL_NAMES,
  SLOT_NAMES,
  WEIGHT_SCALE,
  counted,
  type ItemType,
} from '@game/protocol';

/**
 * Dica de item: o catálogo inteiro na tela, e não só "nome · peso" (PRD §6).
 *
 * O `title` nativo era a versão anterior, e o problema dele não é estética: é uma linha
 * só, sem cor e sem controle — a raridade que o protocolo declara **visível** não tem como
 * aparecer num texto que o navegador desenha cinza sobre bege. Ataque, defesa, nível
 * mínimo e obtenção existiam no catálogo e não existiam no jogo, porque informação que o
 * jogador não vê é informação que não foi escrita.
 *
 * Um elemento só para a página inteira, e não um por slot: só há um cursor, logo só há uma
 * dica visível por vez. Cada slot que quiser dica chama `attachItemTooltip` e o elemento
 * compartilhado é preenchido de novo no `mouseenter` — o que também significa que um
 * inventário de cem slots não cria cem nós escondidos no DOM.
 *
 * `pointer-events: none` no CSS é parte do contrato: a dica flutua perto do cursor, e um
 * elemento que recebesse eventos ali roubaria o `dragover` do slot debaixo dele — o
 * arrastar e soltar quebraria exatamente quando a dica estivesse aberta. Pelo mesmo
 * motivo ela some no `dragstart`: durante o arraste ela só taparia o alvo do drop.
 */

/** Nome de categoria por `ItemKind`. Vocabulário do cliente: o servidor nunca escreve isto. */
const KIND_NAMES: Readonly<Record<ItemKind, string>> = {
  [ItemKind.Generic]: 'Objeto',
  [ItemKind.Weapon]: 'Arma',
  // 'Proteção' e não 'Armadura': a categoria cobre elmo, calça, bota, amuleto e anel, e
  // chamar um anel de armadura seria a dica desmentindo o desenho.
  [ItemKind.Armor]: 'Proteção',
  [ItemKind.Shield]: 'Escudo',
  [ItemKind.Container]: 'Contêiner',
  [ItemKind.Consumable]: 'Consumível',
  [ItemKind.Currency]: 'Moeda',
  [ItemKind.Ammo]: 'Munição',
  [ItemKind.Corpse]: 'Corpo',
  [ItemKind.Rune]: 'Runa',
  [ItemKind.Tool]: 'Ferramenta',
  [ItemKind.Key]: 'Chave',
  [ItemKind.Food]: 'Comida',
};

/**
 * Nome de raridade, local porque o protocolo só declara a **cor** (`RARITY_COLORS`).
 * O nome acompanha a cor de propósito: cor sozinha excluiria quem não a distingue — a
 * mesma decisão do ponto vazado da lista de amigos.
 */
const RARITY_NAMES: Readonly<Record<ItemRarity, string>> = {
  [ItemRarity.Common]: 'Comum',
  [ItemRarity.Uncommon]: 'Incomum',
  [ItemRarity.Rare]: 'Raro',
  [ItemRarity.Epic]: 'Épico',
  // Os dois degraus de §15.11. A cor os separa na mochila; o nome é o que os separa para
  // quem não distingue âmbar de roxo — que é a razão de esta tabela existir ao lado de
  // `RARITY_COLORS` em vez de dentro dela.
  [ItemRarity.Legendary]: 'Lendário',
  [ItemRarity.Celestial]: 'Celestial',
};

/** O elemento compartilhado. Criado no primeiro uso para não tocar o DOM em import. */
let element: HTMLElement | null = null;

function ensure(): HTMLElement {
  if (!element) {
    element = document.createElement('div');
    element.className = 'item-tooltip';
    document.body.append(element);
  }
  return element;
}

/**
 * Esconde a dica de fora do par enter/leave.
 *
 * Existe porque as janelas se redesenham por `innerHTML = ''`: o slot sob o cursor morre
 * sem disparar `mouseleave`, e a dica dele ficaria órfã na tela até o próximo hover. Quem
 * redesenha chama isto primeiro.
 */
export function hideItemTooltip(): void {
  element?.classList.remove('visible');
}

/**
 * Liga a dica a um elemento. `count` é o tamanho da pilha — decide se a linha de peso
 * mostra também o total, que é o número que interessa a quem administra capacidade.
 */
export function attachItemTooltip(target: HTMLElement, type: ItemType, count: number): void {
  target.addEventListener('mouseenter', (event) => {
    const tip = ensure();
    // `textContent = ''` + `append`, nunca `innerHTML`: nome, obtenção e descrição vêm do
    // catálogo hoje, mas a regra de nunca interpolar dado em HTML não tem exceção barata.
    tip.textContent = '';
    tip.append(content(type, count));
    tip.classList.add('visible');
    place(tip, event.clientX, event.clientY);
  });

  target.addEventListener('mousemove', (event) => {
    // Segue o cursor para a dica nunca cobrir o slot vizinho que o jogador vai olhar em
    // seguida; só quando visível, para um mousemove atrasado não ressuscitar dica morta.
    if (element?.classList.contains('visible')) place(element, event.clientX, event.clientY);
  });

  target.addEventListener('mouseleave', hideItemTooltip);
  target.addEventListener('dragstart', hideItemTooltip);
}

/**
 * Posiciona perto do cursor sem sair da viewport: desloca para o lado oposto quando
 * encostaria na borda. Medir depois de preencher é obrigatório — a largura depende do
 * conteúdo, e a descrição varia de zero a duas linhas.
 */
function place(tip: HTMLElement, x: number, y: number): void {
  const OFFSET = 14;
  const MARGIN = 6;
  const rect = tip.getBoundingClientRect();

  let left = x + OFFSET;
  let top = y + OFFSET;
  if (left + rect.width + MARGIN > window.innerWidth) left = x - OFFSET - rect.width;
  if (top + rect.height + MARGIN > window.innerHeight) top = y - OFFSET - rect.height;

  tip.style.left = `${Math.max(MARGIN, left)}px`;
  tip.style.top = `${Math.max(MARGIN, top)}px`;
}

/** Monta as linhas. Só propriedade **presente** vira linha: dica de pão não fala de ataque. */
function content(type: ItemType, count: number): DocumentFragment {
  const fragment = document.createDocumentFragment();

  const name = document.createElement('div');
  name.className = 'item-tooltip-name';
  name.textContent = type.name;
  if (type.rarity !== undefined) {
    // A promessa de `RARITY_COLORS` cumprida também aqui: o nome na cor da raridade, e a
    // palavra junto — a cor diz "diferente" de longe, a palavra diz **quanto**.
    name.style.color = RARITY_COLORS[type.rarity];
    const badge = document.createElement('span');
    badge.className = 'item-tooltip-rarity';
    badge.textContent = ` · ${RARITY_NAMES[type.rarity]}`;
    name.append(badge);
  }
  fragment.append(name);

  const kind = document.createElement('div');
  kind.className = 'item-tooltip-kind';
  // `!== null`, e não truthiness: `ItemSlot.Head` é zero, e um elmo sem linha de slot seria
  // o primeiro sintoma do engano.
  kind.textContent =
    type.slot !== null ? `${KIND_NAMES[type.kind]} · ${SLOT_NAMES[type.slot]}` : KIND_NAMES[type.kind];
  fragment.append(kind);

  // `!== undefined` em todas, pelo mesmo zero: `SkillId.Fist` é 0, e um `if (type.skill)`
  // esconderia a perícia exatamente da vara de pescar, que é a única que treina Punho.
  const lines: string[] = [];
  if (type.attack !== undefined) lines.push(`Ataque +${type.attack}`);
  if (type.defense !== undefined) lines.push(`Defesa +${type.defense}`);
  if (type.skill !== undefined) lines.push(`Perícia: ${SKILL_NAMES[type.skill]}`);
  // O elemento fica logo abaixo da perícia porque responde à mesma pergunta — "contra o que
  // esta arma serve?" — e é a única linha que muda de resposta conforme o alvo. Sem ela, a
  // escolha entre os dois bastões seria feita pela cor da gema, e o jogador que abre a dica
  // não teria como saber que a peça que ele carrega é a que o Elemental de fogo ignora.
  if (type.element !== undefined) lines.push(`Elemento: ${ELEMENT_NAMES[type.element]}`);
  if (type.range !== undefined) lines.push(`Alcance: ${counted(type.range, 'tile', 'tiles')}`);
  if (type.capacity !== undefined) lines.push(`Capacidade: ${counted(type.capacity, 'slot', 'slots')}`);
  if (type.healsHealth !== undefined) lines.push(`Cura ${type.healsHealth} de vida`);
  if (type.healsMana !== undefined) lines.push(`Cura ${type.healsMana} de mana`);
  if (type.feedsMs !== undefined) lines.push(`Saciedade: ${minutes(type.feedsMs)}`);
  if (type.charges !== undefined) lines.push(`Cargas: ${type.charges}`);
  if (type.durability !== undefined)
    lines.push(`Durabilidade: ${counted(type.durability, 'golpe', 'golpes')}`);
  if (type.minLevel !== undefined) lines.push(`Nível mínimo: ${type.minLevel}`);
  if (type.minMagicLevel !== undefined) lines.push(`Magia mínima: ${type.minMagicLevel}`);

  // Peso sempre: é a única propriedade universal do catálogo. Com pilha, o total também —
  // "0.7 oz" não responde nada sobre cem flechas; "70.0 oz" responde.
  lines.push(
    count > 1
      ? `Peso: ${weight(type.weight)} · pilha: ${weight(type.weight * count)}`
      : `Peso: ${weight(type.weight)}`,
  );
  // Valor zero fica de fora: é o corpo, e "Valor: 0 gp" leria como convite a vendê-lo.
  if (type.value > 0) lines.push(`Valor: ${type.value} gp`);

  for (const text of lines) {
    const line = document.createElement('div');
    line.className = 'item-tooltip-line';
    line.textContent = text;
    fragment.append(line);
  }

  // A obtenção é o campo que §15.5 chama de não-decorativo: é aqui que ele vira jogo — o
  // jogador que vê a peça na mochila de outro lê nesta linha o degrau que falta subir.
  if (type.source) {
    const source = document.createElement('div');
    source.className = 'item-tooltip-source';
    source.textContent = `Obtenção: ${type.source}`;
    fragment.append(source);
  }

  // Sem descrição, sem linha: o fallback "Você vê..." é do olhar no chão, não da dica.
  if (type.description) {
    const description = document.createElement('div');
    description.className = 'item-tooltip-description';
    description.textContent = type.description;
    fragment.append(description);
  }

  return fragment;
}

function weight(units: number): string {
  return `${(units / WEIGHT_SCALE).toFixed(1)} oz`;
}

/**
 * `feedsMs` em minutos legíveis. O catálogo só tem múltiplos de minuto; o ramo com decimal
 * é rede de segurança para o item futuro que quebrar a regra sem avisar esta função.
 */
function minutes(feedsMs: number): string {
  const value = feedsMs / 60_000;
  return Number.isInteger(value) ? counted(value, 'minuto', 'minutos') : `${value.toFixed(1)} minutos`;
}
